import { CheckCircle2, XCircle, AlertTriangle, Info, X } from "lucide-react";
import { dismissToast, useToasts, type Toast } from "@/lib/toast";
import { cn } from "@/lib/cn";

// Floating toast stack at the top-right of the viewport. Subscribes to the
// module-level toast queue. Confirm-style toasts render with yes/no
// buttons; auto-dismissing toasts only show an "X" to dismiss early.
export function ToastContainer(): JSX.Element | null {
  const toasts = useToasts();
  if (toasts.length === 0) return null;
  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[60] flex w-[360px] max-w-[92vw] flex-col gap-2">
      {toasts.map((t) => (
        <ToastCard key={t.id} t={t} />
      ))}
    </div>
  );
}

function ToastCard({ t }: { t: Toast }): JSX.Element {
  const { icon: Icon, ringClass, iconClass } = visualForTone(t.tone);
  return (
    <div
      className={cn(
        "pointer-events-auto flex items-start gap-2.5 rounded-lg border bg-white px-3 py-2.5 shadow-md",
        "animate-in fade-in slide-in-from-top-2 duration-200",
        ringClass,
      )}
      role={t.tone === "error" ? "alert" : "status"}
    >
      <Icon className={cn("mt-0.5 h-4 w-4 flex-shrink-0", iconClass)} />
      <div className="min-w-0 flex-1 text-[13px] leading-relaxed text-zinc-800">
        {t.message}
        {t.confirm ? (
          <div className="mt-2 flex justify-end gap-1.5">
            <button
              type="button"
              onClick={t.confirm.onNo}
              className="rounded-md px-2.5 py-1 text-[12px] text-zinc-600 hover:bg-zinc-100"
            >
              {t.confirm.noLabel}
            </button>
            <button
              type="button"
              onClick={t.confirm.onYes}
              className={cn(
                "rounded-md px-2.5 py-1 text-[12px] font-medium text-white",
                t.confirm.yesTone === "danger"
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-zinc-900 hover:bg-zinc-700",
              )}
            >
              {t.confirm.yesLabel}
            </button>
          </div>
        ) : null}
      </div>
      {/* Confirm toasts don't auto-dismiss; users pick yes/no. Plain toasts
          get an X for early dismissal. */}
      {!t.confirm ? (
        <button
          type="button"
          onClick={() => dismissToast(t.id)}
          className="-mr-1 flex-shrink-0 rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
          aria-label="关闭"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}

function visualForTone(tone: Toast["tone"]): {
  icon: typeof CheckCircle2;
  ringClass: string;
  iconClass: string;
} {
  switch (tone) {
    case "success":
      return {
        icon: CheckCircle2,
        ringClass: "border-emerald-200",
        iconClass: "text-emerald-600",
      };
    case "error":
      return {
        icon: XCircle,
        ringClass: "border-red-200",
        iconClass: "text-red-600",
      };
    case "warn":
      return {
        icon: AlertTriangle,
        ringClass: "border-amber-200",
        iconClass: "text-amber-600",
      };
    default:
      return {
        icon: Info,
        ringClass: "border-zinc-200",
        iconClass: "text-zinc-500",
      };
  }
}
